/**
 * MM ESSENTIELS - Product Page JavaScript
 * Handles: variant selection, thumbnails, quantity, cart AJAX
 */

if (!customElements.get('mm-product-form')) {
  class MMProductForm extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      /* Wait for DOM children to be parsed */
      requestAnimationFrame(() => {
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('[name="id"]');
        this.quantityInput = this.querySelector('[name="quantity"]');
        this.product = JSON.parse(this.dataset.product || '{}');
        this.variants = this.product.variants || [];
        this.options = {};
        this.currentVariant = null;
        this.init();
      });
    }

    init() {
      this.initOptions();
      this.bindThumbnails();
      this.bindColorSwatches();
      this.bindSizeButtons();
      this.bindQuantity();
      this.bindAddToCart();
      this.bindBuyNow();
      this.bindZoom();
      this.updateFromInitialVariant();
    }

    initOptions() {
      const optionEls = this.querySelectorAll('[data-option-name]');
      optionEls.forEach(el => {
        const name = el.dataset.optionName;
        const value = el.dataset.optionValue;
        if (el.classList.contains('is-active')) {
          this.options[name] = value;
        }
      });
    }

    updateFromInitialVariant() {
      const variantId = this.variantInput ? this.variantInput.value : null;
      if (variantId) {
        this.currentVariant = this.variants.find(v => String(v.id) === String(variantId));
      }
      if (!this.currentVariant && this.variants.length > 0) {
        this.currentVariant = this.variants.find(v => v.available) || this.variants[0];
      }
      if (this.currentVariant) {
        this.updateUI();
      }
    }

    /* --- Thumbnails --- */
    bindThumbnails() {
      const thumbnails = this.querySelectorAll('.mm-product__thumbnail');
      const mainImage = this.querySelector('.mm-product__main-image');
      const dots = this.querySelectorAll('.mm-product__dot');

      thumbnails.forEach((thumb, idx) => {
        thumb.addEventListener('click', () => {
          thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          if (mainImage) {
            mainImage.src = thumb.querySelector('img').src;
            mainImage.srcset = thumb.querySelector('img').dataset.fullSrc || thumb.querySelector('img').src;
          }
          dots.forEach(d => d.classList.remove('is-active'));
          if (dots[idx]) dots[idx].classList.add('is-active');
        });
      });

      dots.forEach((dot, idx) => {
        dot.addEventListener('click', () => {
          const carousel = this.querySelector('.mm-product__mobile-carousel');
          const slides = this.querySelectorAll('.mm-product__mobile-slide');
          if (carousel && slides[idx]) {
            slides[idx].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
          dots.forEach(d => d.classList.remove('is-active'));
          dot.classList.add('is-active');
          thumbnails.forEach(t => t.classList.remove('is-active'));
          if (thumbnails[idx]) thumbnails[idx].classList.add('is-active');
          if (mainImage && slides[idx]) {
            const img = slides[idx].querySelector('img');
            if (img) mainImage.src = img.src;
          }
        });
      });

      /* Mobile carousel scroll observer */
      const carousel = this.querySelector('.mm-product__mobile-carousel');
      if (carousel) {
        const slides = carousel.querySelectorAll('.mm-product__mobile-slide');
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const idx = Array.from(slides).indexOf(entry.target);
              dots.forEach(d => d.classList.remove('is-active'));
              if (dots[idx]) dots[idx].classList.add('is-active');
              thumbnails.forEach(t => t.classList.remove('is-active'));
              if (thumbnails[idx]) thumbnails[idx].classList.add('is-active');
              if (mainImage) {
                const img = entry.target.querySelector('img');
                if (img) mainImage.src = img.src;
              }
            }
          });
        }, { root: carousel, threshold: 0.5 });

        slides.forEach(slide => observer.observe(slide));
      }
    }

    /* --- Color Swatches --- */
    bindColorSwatches() {
      const swatches = this.querySelectorAll('.mm-block-color__swatch');
      swatches.forEach(swatch => {
        swatch.addEventListener('click', () => {
          swatches.forEach(s => s.classList.remove('is-active'));
          swatch.classList.add('is-active');
          this.options[swatch.dataset.optionName] = swatch.dataset.optionValue;

          const label = this.querySelector('.mm-block-color__selected');
          if (label) label.textContent = swatch.dataset.optionValue;

          this.onOptionChange();
        });
      });
    }

    /* --- Size Buttons --- */
    bindSizeButtons() {
      const buttons = this.querySelectorAll('.mm-block-size__btn');
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-unavailable')) return;
          buttons.forEach(b => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          this.options[btn.dataset.optionName] = btn.dataset.optionValue;

          const label = this.querySelector('.mm-block-size__selected');
          if (label) label.textContent = btn.dataset.optionValue;

          this.onOptionChange();
        });
      });
    }

    /* --- Quantity --- */
    bindQuantity() {
      const minus = this.querySelector('.mm-block-quantity__btn--minus');
      const plus = this.querySelector('.mm-block-quantity__btn--plus');
      const display = this.querySelector('.mm-block-quantity__value');

      if (minus) {
        minus.addEventListener('click', () => {
          let val = parseInt(this.quantityInput.value) || 1;
          val = Math.max(1, val - 1);
          this.quantityInput.value = val;
          if (display) display.textContent = val;
        });
      }

      if (plus) {
        plus.addEventListener('click', () => {
          let val = parseInt(this.quantityInput.value) || 1;
          val++;
          this.quantityInput.value = val;
          if (display) display.textContent = val;
        });
      }
    }

    /* --- Add to Cart --- */
    bindAddToCart() {
      const btn = this.querySelector('.mm-block-buy__add-to-cart');
      if (!btn || !this.form) return;

      this.form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!this.currentVariant || !this.currentVariant.available) return;

        btn.disabled = true;
        btn.textContent = btn.dataset.addingText || 'ADDING...';

        try {
          const formData = {
            items: [{
              id: parseInt(this.variantInput.value),
              quantity: parseInt(this.quantityInput.value) || 1,
            }]
          };

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData),
          });

          if (response.ok) {
            btn.textContent = btn.dataset.addedText || 'ADDED!';
            const qty = parseInt(this.quantityInput.value) || 1;
            /* Dispatch cart:update event matching Horizon theme format */
            this.dispatchCartEvent(qty);
            setTimeout(() => {
              btn.textContent = btn.dataset.addText || 'ADD TO CART';
              btn.disabled = false;
            }, 2000);
          } else {
            const error = await response.json();
            btn.textContent = error.description || 'ERROR';
            setTimeout(() => {
              btn.textContent = btn.dataset.addText || 'ADD TO CART';
              btn.disabled = false;
            }, 2000);
          }
        } catch {
          btn.textContent = btn.dataset.addText || 'ADD TO CART';
          btn.disabled = false;
        }
      });
    }

    /* --- Buy Now --- */
    bindBuyNow() {
      const btn = this.querySelector('.mm-block-buy__now');
      if (!btn) return;

      btn.addEventListener('click', () => {
        if (!this.currentVariant || !this.currentVariant.available) return;
        const qty = parseInt(this.quantityInput.value) || 1;
        window.location.href = `/cart/${this.currentVariant.id}:${qty}`;
      });
    }

    /* --- Lightbox / Zoom --- */
    bindZoom() {
      const lightbox = this.querySelector('.mm-lightbox');
      if (!lightbox) return;

      const lbImage = lightbox.querySelector('.mm-lightbox__image');
      const lbClose = lightbox.querySelector('.mm-lightbox__close');
      const lbPrev = lightbox.querySelector('.mm-lightbox__nav--prev');
      const lbNext = lightbox.querySelector('.mm-lightbox__nav--next');
      const lbThumbs = lightbox.querySelectorAll('.mm-lightbox__thumb');
      const lbCounter = lightbox.querySelector('.mm-lightbox__current');
      const zoomBtn = this.querySelector('.mm-product__zoom-btn');
      const mainImage = this.querySelector('.mm-product__main-image');

      let currentIndex = 0;
      let isZoomed = false;
      const totalImages = lbThumbs.length;

      const getSources = () => Array.from(lbThumbs).map(t => ({
        src: t.dataset.src,
        alt: t.dataset.alt
      }));

      const showImage = (index) => {
        const sources = getSources();
        if (!sources[index]) return;
        currentIndex = index;
        lbImage.src = sources[index].src;
        lbImage.alt = sources[index].alt;
        lbImage.classList.remove('is-zoomed');
        lbImage.style.transform = '';
        isZoomed = false;
        if (lbCounter) lbCounter.textContent = index + 1;
        lbThumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
      };

      const openLightbox = (index) => {
        showImage(index || 0);
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      };

      const closeLightbox = () => {
        lightbox.classList.remove('is-open');
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      };

      /* Open from zoom button */
      if (zoomBtn) {
        zoomBtn.addEventListener('click', () => {
          const thumbnails = this.querySelectorAll('.mm-product__thumbnail');
          const activeThumb = this.querySelector('.mm-product__thumbnail.is-active');
          const idx = activeThumb ? Array.from(thumbnails).indexOf(activeThumb) : 0;
          openLightbox(Math.max(0, idx));
        });
      }

      /* Open from main image click */
      if (mainImage) {
        mainImage.style.cursor = 'zoom-in';
        mainImage.addEventListener('click', () => {
          const thumbnails = this.querySelectorAll('.mm-product__thumbnail');
          const activeThumb = this.querySelector('.mm-product__thumbnail.is-active');
          const idx = activeThumb ? Array.from(thumbnails).indexOf(activeThumb) : 0;
          openLightbox(Math.max(0, idx));
        });
      }

      /* Open from mobile carousel image tap */
      const mobileSlides = this.querySelectorAll('.mm-product__mobile-slide img');
      mobileSlides.forEach((img, idx) => {
        img.style.cursor = 'zoom-in';
        img.addEventListener('click', () => openLightbox(idx));
      });

      /* Close */
      if (lbClose) lbClose.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox || e.target.classList.contains('mm-lightbox__image-wrap')) {
          closeLightbox();
        }
      });

      /* Navigation */
      if (lbPrev) lbPrev.addEventListener('click', () => showImage((currentIndex - 1 + totalImages) % totalImages));
      if (lbNext) lbNext.addEventListener('click', () => showImage((currentIndex + 1) % totalImages));

      /* Thumbnails in lightbox */
      lbThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => showImage(parseInt(thumb.dataset.index)));
      });

      /* Keyboard navigation */
      document.addEventListener('keydown', (e) => {
        if (!lightbox.classList.contains('is-open')) return;
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowLeft') showImage((currentIndex - 1 + totalImages) % totalImages);
        if (e.key === 'ArrowRight') showImage((currentIndex + 1) % totalImages);
      });

      /* Click image to toggle zoom */
      lbImage.addEventListener('click', (e) => {
        if (isZoomed) {
          lbImage.classList.remove('is-zoomed');
          lbImage.style.transform = '';
          isZoomed = false;
        } else {
          lbImage.classList.add('is-zoomed');
          lbImage.style.transform = 'scale(2)';
          isZoomed = true;
        }
      });

      /* Pan when zoomed */
      let isDragging = false;
      let startX, startY, translateX = 0, translateY = 0;

      lbImage.addEventListener('mousedown', (e) => {
        if (!isZoomed) return;
        isDragging = true;
        startX = e.clientX - translateX;
        startY = e.clientY - translateY;
        lbImage.style.cursor = 'grabbing';
        e.preventDefault();
      });

      document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        translateX = e.clientX - startX;
        translateY = e.clientY - startY;
        lbImage.style.transform = `scale(2) translate(${translateX / 2}px, ${translateY / 2}px)`;
      });

      document.addEventListener('mouseup', () => {
        isDragging = false;
        if (isZoomed && lbImage) lbImage.style.cursor = 'zoom-out';
      });

      /* Touch support for pan */
      lbImage.addEventListener('touchstart', (e) => {
        if (!isZoomed || e.touches.length !== 1) return;
        isDragging = true;
        startX = e.touches[0].clientX - translateX;
        startY = e.touches[0].clientY - translateY;
      }, { passive: true });

      lbImage.addEventListener('touchmove', (e) => {
        if (!isDragging || e.touches.length !== 1) return;
        translateX = e.touches[0].clientX - startX;
        translateY = e.touches[0].clientY - startY;
        lbImage.style.transform = `scale(2) translate(${translateX / 2}px, ${translateY / 2}px)`;
        e.preventDefault();
      }, { passive: false });

      lbImage.addEventListener('touchend', () => {
        isDragging = false;
      });
    }

    /* --- Variant Matching --- */
    onOptionChange() {
      const matched = this.variants.find(variant => {
        return variant.options.every((optVal, idx) => {
          const optName = this.product.options[idx];
          return !this.options[optName] || this.options[optName] === optVal;
        });
      });

      if (matched) {
        this.currentVariant = matched;
        this.variantInput.value = matched.id;
        this.updateUI();
        this.updateURL();
      }
    }

    updateUI() {
      const v = this.currentVariant;
      if (!v) return;

      /* Price */
      const priceEl = this.querySelector('.mm-block-price__current');
      const compareEl = this.querySelector('.mm-block-price__compare');
      const badgeEl = this.querySelector('.mm-block-price__badge');

      if (priceEl) {
        priceEl.textContent = this.formatMoney(v.price);
      }
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.textContent = this.formatMoney(v.compare_at_price);
          compareEl.style.display = '';
          if (badgeEl) {
            const pct = Math.round((1 - v.price / v.compare_at_price) * 100);
            badgeEl.textContent = `-${pct}%`;
            badgeEl.style.display = '';
          }
        } else {
          compareEl.style.display = 'none';
          if (badgeEl) badgeEl.style.display = 'none';
        }
      }

      /* Add to cart state */
      const addBtn = this.querySelector('.mm-block-buy__add-to-cart');
      const buyBtn = this.querySelector('.mm-block-buy__now');
      if (addBtn) {
        addBtn.disabled = !v.available;
        addBtn.textContent = v.available
          ? (addBtn.dataset.addText || 'ADD TO CART')
          : (addBtn.dataset.soldOutText || 'SOLD OUT');
      }
      if (buyBtn) {
        buyBtn.disabled = !v.available;
        buyBtn.style.display = v.available ? '' : 'none';
      }

      /* Update variant image */
      if (v.featured_image) {
        const mainImage = this.querySelector('.mm-product__main-image');
        if (mainImage) {
          mainImage.src = v.featured_image.src;
        }
      }

      /* Update size availability */
      this.updateSizeAvailability();

      /* Shipping bar */
      this.updateShippingBar(v.price);
    }

    updateSizeAvailability() {
      const sizeButtons = this.querySelectorAll('.mm-block-size__btn');
      sizeButtons.forEach(btn => {
        const sizeVal = btn.dataset.optionValue;
        const sizeName = btn.dataset.optionName;
        const hasAvailable = this.variants.some(v => {
          const sizeIdx = this.product.options.indexOf(sizeName);
          if (sizeIdx === -1) return true;
          const matchesSize = v.options[sizeIdx] === sizeVal;
          const matchesOther = v.options.every((optVal, idx) => {
            if (idx === sizeIdx) return true;
            const optName = this.product.options[idx];
            return !this.options[optName] || this.options[optName] === optVal;
          });
          return matchesSize && matchesOther && v.available;
        });
        btn.classList.toggle('is-unavailable', !hasAvailable);
      });
    }

    updateShippingBar(price) {
      const bar = this.querySelector('.mm-block-shipping-bar');
      if (!bar) return;
      const threshold = parseInt(bar.dataset.threshold) || 12500;
      const fill = bar.querySelector('.mm-block-shipping-bar__fill');
      const text = bar.querySelector('.mm-block-shipping-bar__text');
      const priceCents = price;
      const remaining = Math.max(0, threshold - priceCents);
      const pct = Math.min(100, (priceCents / threshold) * 100);

      if (fill) fill.style.width = pct + '%';
      if (text && remaining > 0) {
        text.innerHTML = `You're <strong>${this.formatMoney(remaining)}</strong> away from free shipping!`;
      } else if (text) {
        text.innerHTML = 'You qualify for <strong>free shipping</strong>!';
      }
    }

    updateURL() {
      if (!this.currentVariant) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url);
    }

    dispatchCartEvent(itemCount) {
      /* Match Horizon theme CartAddEvent structure (from events.js) */
      const event = new Event('cart:update', { bubbles: true });
      event.detail = {
        resource: {},
        sourceId: 'mm-product-form',
        data: {
          source: 'product-form-component',
          itemCount: itemCount || 1,
        },
      };
      document.dispatchEvent(event);
    }

    formatMoney(cents) {
      const moneyFormat = window.mmMoneyFormat || '{{amount_with_comma_separator}} €';
      const amount = (cents / 100).toFixed(2);
      const amountWithComma = amount.replace('.', ',');
      return moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals}}', Math.round(cents / 100))
        .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    }
  }

  customElements.define('mm-product-form', MMProductForm);
}

/* Bundle Add to Cart */
document.addEventListener('DOMContentLoaded', () => {
  const bundleBtns = document.querySelectorAll('.mm-bundle__summary-btn');
  bundleBtns.forEach(btn => {
    btn.addEventListener('click', async () => {
      const ids = btn.dataset.variantIds;
      if (!ids) return;
      const items = ids.split(',').map(id => ({ id: parseInt(id.trim()), quantity: 1 }));

      btn.disabled = true;
      btn.textContent = 'ADDING...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (res.ok) {
          btn.textContent = 'ADDED TO CART!';
          /* Dispatch cart:update matching Horizon theme format */
          const bundleEvent = new Event('cart:update', { bubbles: true });
          bundleEvent.detail = {
            resource: {},
            sourceId: 'mm-bundle',
            data: {
              source: 'product-form-component',
              itemCount: items.length,
            },
          };
          document.dispatchEvent(bundleEvent);
          setTimeout(() => {
            btn.textContent = btn.dataset.text || 'ADD BUNDLE TO CART';
            btn.disabled = false;
          }, 2000);
        }
      } catch {
        btn.textContent = btn.dataset.text || 'ADD BUNDLE TO CART';
        btn.disabled = false;
      }
    });
  });
});
